import { useContext, useEffect } from "react";
import { UserContext } from "../context/UserContext";
import { fetchMyProfileCall } from "../api/userApiCalls";
import { getSessionToken } from "../api/client";

/**
 * Loads the logged-in user's profile into context once, when a session token exists and the
 * user isn't loaded yet. 401s (expired/invalid token) are handled globally by the response
 * interceptor, so there's no auth handling here.
 *
 * The token read goes through the platform adapter and is awaited: the web returns it
 * synchronously from sessionStorage, but the mobile keychain read is a Promise. The effect
 * guards against setting state after unmount, since that await introduces a gap where the
 * component can go away mid-flight.
 */
function useFetchMyProfile() {
  const { user, setUser } = useContext(UserContext);

  useEffect(() => {
    if (user.id !== 0) return;

    let cancelled = false;
    (async () => {
      try {
        const token = await getSessionToken();
        if (!token || cancelled) return;
        const response = await fetchMyProfileCall();
        if (!cancelled) setUser({ ...response.data });
      } catch (error) {
        console.error(error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [setUser, user.id]);
}

export default useFetchMyProfile;
